import { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import SideSheetModal from "@/components/SideSheetModal";
import type { MaterialNames } from "@/components/material_symbol_names";
import { getInteractionDisplayName, type Interaction } from "@/components/interactions";
import InteractionSideSheetContent, { getInteractionWithLabel } from "./InteractionSideSheetContent.tsx";
import InteractionSideSheetSkeleton from "./InteractionSideSheetSkeleton.tsx";
import { CRM_INTERACTION_QUERY } from "./queries";

export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}

export type InteractionSideSheetProps = {
  open: boolean;
  onClose: () => void;
  interactionId: string | null;
  pgaId: string | null | undefined;
  title?: React.ReactNode;
  customActionButtons?: CustomActionButton[];
};

const DEFAULT_SIDE_SHEET_TITLE = "Interaction Details";

export default function InteractionSideSheet({
  open,
  onClose,
  interactionId,
  pgaId,
  title: titleProp,
  customActionButtons
}: InteractionSideSheetProps) {
  const { data, loading, error } = useQuery(CRM_INTERACTION_QUERY, {
    variables: { id: interactionId || "" },
    fetchPolicy: "network-only",
    skip: !open || !interactionId
  });

  const interaction = data?.crmInteraction as Interaction | null | undefined;
  const isRestrictedPrivateInteraction = !!interaction?.lastMessage.isPrivate && !interaction.lastMessage.haveAccess;

  const headerTitle = useMemo(() => {
    if (!interaction || titleProp !== undefined) return titleProp ?? DEFAULT_SIDE_SHEET_TITLE;
    const withLabel = getInteractionWithLabel(interaction, pgaId);
    const displayName = getInteractionDisplayName(interaction, withLabel);
    return (
      <span className="whitespace-normal break-words font-sans" data-testid="interaction-side-sheet-title">
        {displayName}
      </span>
    );
  }, [interaction, pgaId, titleProp]);

  useEffect(() => {
    if (open && isRestrictedPrivateInteraction) {
      onClose();
    }
  }, [isRestrictedPrivateInteraction, onClose, open]);

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) {
      onClose();
    }
  }

  if (isRestrictedPrivateInteraction) {
    return null;
  }

  return (
    <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title={headerTitle} actions={null}>
      {loading ? (
        <InteractionSideSheetSkeleton />
      ) : error ? (
        <ErrorBoundary variant="inline" title="Unable to Load Interaction" message={error.message} />
      ) : interaction ? (
        <InteractionSideSheetContent
          interaction={interaction}
          pgaId={pgaId}
          customActionButtons={customActionButtons}
        />
      ) : open ? (
        <EmptyState iconName="record_voice_over" title="Interaction Not Found" />
      ) : null}
    </SideSheetModal>
  );
}
